import React, {
    Dispatch,
    FC,
    SetStateAction,
    useEffect,
    useRef,
    useState,
} from "react";
import { useAppSelector as useSelector } from "../../../../store";
import Location from "../../Icons/Location";

/**
 * Range Selector Props Interface
 * @interface RangeSelectorProps
 */
interface RangeSelectorProps {
  /** minimum number */
  min: number;
  /** maximum number */
  max: number;
  /** callback value to update the maximum distance */
  setMaxDistance: Dispatch<SetStateAction<number>>;
  /** additional class to be added */
  rangeType: string;
  /** callback value to update the button disabled or not */
  setIsLoadingSuburbs: Dispatch<SetStateAction<boolean>>;
  /** callback value to show an alert if max distance entered greater than 100 */
  setShowMaxDistanceAlert: Dispatch<SetStateAction<boolean>>;
}

const RangeSelector: FC<RangeSelectorProps> = ({
  min,
  max,
  setMaxDistance,
  setIsLoadingSuburbs,
  setShowMaxDistanceAlert,
  rangeType,
}) => {
  const jobpreferences = useSelector(
    (state) =>
      state.onboarding.jobpreferences ??
      state.superadmin.reviewCandidateData?.jobpreference?.[0]
  );
  const max_distance = jobpreferences?.max_travel_distance;

  const [value, setValue] = useState<number>(max_distance || 0);
  const [isEmpty, setIsEmpty] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleIncrement = () => {
    if (value < max) {
      setValue((prev) => prev + 1);
      setIsLoadingSuburbs(false);
    }
  };

  const handleDecrement = () => {
    if (value > min) {
      setValue((prev) => prev - 1);
      setIsLoadingSuburbs(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowMaxDistanceAlert(false);

    // Extract only numbers from the input, ignoring 'km' and other characters
    const numbersOnly = e.target.value.replace(/[^0-9]/g, "");

    // If no numbers, mark as empty but don't set value to 0 yet
    if (numbersOnly === "") {
      setIsEmpty(true);
      setIsLoadingSuburbs(true);
      return;
    }

    const newValue = Number(numbersOnly);

    // Check if value exceeds 100
    if (newValue > 100) {
      setShowMaxDistanceAlert(true);
      return;
    }

    // Update value if within valid range
    if (newValue >= min && newValue <= max) {
      setIsEmpty(false);
      // Only update if value actually changed
      if (newValue !== value) {
        setValue(newValue);
        setIsLoadingSuburbs(false); // Enable button when value changes
      }
      // If value is the same, don't change anything
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const cursorPos = input.selectionStart || 0;
    const inputValue = input.value;

    // Handle backspace to prevent deleting 'km' and maintain proper cursor position
    if (e.key === "Backspace") {
      const numberPart = inputValue.replace("km", "");

      // If cursor is at the end or after the numbers (before 'km')
      if (cursorPos >= numberPart.length) {
        e.preventDefault();

        if (numberPart.length > 0) {
          // Remove last digit
          const newNumber = numberPart.slice(0, -1);

          if (newNumber === "") {
            // Mark as empty when all digits removed
            setIsEmpty(true);
            setIsLoadingSuburbs(true); // Disable button when empty
          } else {
            const newValue = Number(newNumber);
            if (newValue >= min && newValue <= max) {
              setIsEmpty(false);
              setValue(newValue);
              setIsLoadingSuburbs(false); // Enable button when value changes
            }
          }

          // Set cursor position after the remaining numbers
          setTimeout(() => {
            if (inputRef.current) {
              const newPos = newNumber.length;
              inputRef.current.setSelectionRange(newPos, newPos);
            }
          }, 0);
        }
      }
    }

    // Prevent cursor from moving into 'km' part
    setTimeout(() => {
      if (inputRef.current) {
        const currentPos = inputRef.current.selectionStart || 0;
        const numberLength = inputRef.current.value.replace("km", "").length;

        if (currentPos > numberLength) {
          inputRef.current.setSelectionRange(numberLength, numberLength);
        }
      }
    }, 0);
  };

  const handleBlur = () => {
    // If empty when blur occurs, set to 0
    if (isEmpty) {
      setValue(0);
      setIsEmpty(false);
      setIsLoadingSuburbs(true);
    }
  };

  useEffect(() => {
    // if (value !== 0) {
    setMaxDistance(value);
    // }
  }, [value, setMaxDistance]);

  return (
    <div>
      <div className="d-flex gap-5 align-items-center">
        <svg
          className="cursor-pointer"
          onClick={handleDecrement}
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width="40" height="40" rx="8" fill="#51C2B9" />
          <path
            d="M12.0776 20.953V18.982H26.9276V20.953H12.0776Z"
            fill="black"
          />
        </svg>
        <div className={"form-group w-120px " + rangeType}>
          <input
            ref={inputRef}
            className={`form-control ${
              value > 0 ? "text-black" : "text-grey-medium"
            }`}
            type="text"
            min={min}
            max={max}
            value={isEmpty ? "km" : value + "km"}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            // readOnly
          />
          <Location />
        </div>
        <svg
          className="cursor-pointer"
          onClick={handleIncrement}
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width="40" height="40" rx="8" fill="#51C2B9" />
          <path
            d="M18.4643 26.87V13.25H20.5943V26.87H18.4643ZM12.7043 21.14V19.01H26.2943V21.14H12.7043Z"
            fill="black"
          />
        </svg>
      </div>
    </div>
  );
};

export default RangeSelector;
